import { message, Row } from 'antd';
import { ParamContextProvider, useParamContext } from 'contexts/ParamContext';
import { Form as FormRFF } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { IoIosArrowBack } from 'react-icons/io';
import { useMutation } from 'react-query';
import { camelToFlatCase } from 'utils/camelToFlatCase';
import { defaultDocumentInfoModelProps } from 'utils/documentInfoHelper';

import API from 'api';
import Button from 'components/Buttons';
import Card from 'components/Card';
import DocumentForm from 'components/DocumentForm';
import Form from 'components/Form';
import Title from 'components/Title';
import { useAuthenticatedContext } from 'hooks/useAuthenticated';
import useCategoriesQuery from 'hooks/useCategoriesQuery';
import useDocumentDetails from 'hooks/useDocumentDetails';
import useValidationSchema from 'hooks/useValidationSchema';
import { CertificateDetailsModel } from 'models/certificate.model';
import { CATEGORY_TYPE } from 'models/documents.model';
import AccessDenied from 'pages/AccessDenied';
import { generateBasicPDF } from 'pages/NewCertPage/helpers/generateBasicPDF';

import { parseAdditionalDetails } from './parseAdditionalDetails';
import { useConfig } from './useConfig';
import { useSchema } from './useSchema';

const NewPrivateDocument = () => {
  const { currentUser } = useAuthenticatedContext();
  const { t } = useTranslation();
  const category = useParamContext<CATEGORY_TYPE>();
  const { onBack } = useDocumentDetails();
  const { data: categories } = useCategoriesQuery();
  const { recipient, receiverPublisherId, formFields } = useConfig(category) || {};
  const schema = useSchema(formFields);
  const validate = useValidationSchema(schema);

  const { mutate: publishPrivateDocument, isLoading } = useMutation(
    API.documents.postDocumentsPrivate,
    {
      onSuccess: () => {
        message.success(
          t('success.newDocument', {
            category: t(`certificate.${category}`),
          })
        );
        onBack();
      },
      onError: () => {
        message.error(
          t('error.newDocument', {
            category: t(`certificate.${category}`),
          })
        );
      },
    }
  );

  const handleSubmit = (values: CertificateDetailsModel) => {
    const categoryId = categories?.[category];
    const issuer = currentUser?.name;

    if (!categoryId || !issuer) {
      message.error(
        t('error.newDocument', {
          category: t(`certificate.${category}`),
        })
      );
      return;
    }

    const documentInfo = {
      ...defaultDocumentInfoModelProps,
      title: camelToFlatCase(category),
      categoryId,
      receiverPublisherId,
      additionalDetails: {
        issuer: currentUser.name,
        ...parseAdditionalDetails(values),
      },
    };

    publishPrivateDocument({ documentInfo, documentContent: generateBasicPDF() });
  };

  if (!currentUser?.subCategories || !currentUser.subCategories.includes(category)) {
    return <AccessDenied />;
  }

  return (
    <>
      <Row justify="space-between">
        <Button type="text" $uppercase icon={<IoIosArrowBack />} onClick={onBack}>
          {t('nav.back')}
        </Button>
      </Row>

      <Card hideBackButton padding="medium" style={{ marginTop: '1rem' }}>
        <Title>{`${t('document.new')} ${t(`certificate.${category}`)}`}</Title>
        <FormRFF<CertificateDetailsModel>
          onSubmit={handleSubmit}
          validate={validate}
          initialValues={{
            recipient,
          }}
        >
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit} data-cy="form">
              {formFields && <DocumentForm formFields={formFields} />}

              <Row justify="end">
                <Button
                  type="primary"
                  htmlType="submit"
                  data-cy="form_submit"
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {t('certificate.publish')}
                </Button>
              </Row>
            </Form>
          )}
        </FormRFF>
      </Card>
    </>
  );
};

const NewPrivateDocumentPage = () => (
  <ParamContextProvider paramName="category" enumName={CATEGORY_TYPE}>
    <NewPrivateDocument />
  </ParamContextProvider>
);

export default NewPrivateDocumentPage;
